import { Component, Input, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, Subject, combineLatest } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { selectIsISOCompliant, selectPendForRelease, selectStagedTransactionFormItExtraPrompts, selectStagedTransactionType, selectTransactionsTableCurrentTemplateId, selectTransferType, selectUseStandingAuth } from '../../../create-request/reducers';
import { FeatureFlagService } from '../../services/feature-flag-service';
import { selectHasAccessInitiateMoneyMovement, selectHasPositionLotsPhase2 } from '../../../authorize/reducers';
import { pageInContext, transferTypes } from '../../utilities/Constants';
import { selectTemplatesList } from '../../../account-info-header/reducers';
import { selectViewInContext } from '../../reducers';
import { selectInitReleaseTransactionInContext } from '../../../initiate-and-release/reducers';
import { ItExtraPrompts } from '../../../create-request/models/form/TransactionForm';

@Component({
  standalone: false,
  selector: 'mm-transaction-review',
  templateUrl: './transaction-review.component.html',
  styleUrls: ['./transaction-review.component.scss']
})
export class TransactionReviewComponent implements OnInit {
  @Input() hasWireServiceError: boolean;
  @Input() isTemplateView: boolean;
  subscriptionNotifier: Subject<boolean> = new Subject()
  transactionType: string;
  positionLotsPhase2EnabledFsg: boolean = false;
  useMultipleIt: boolean = false;
  useSloa$: Observable<boolean>;
  isISOCompliant: boolean = false;
  isISOCompliantTemplate: boolean = false;
  destroy$: Subject<boolean> = new Subject()
  hasMmInitiateAccess: boolean = false;
  isPendForRelease: boolean = false;
  isInRTxnInContext: boolean = false;
  useStandingAuth: boolean = false;
  currentStep: string;
  extraPrompts: ItExtraPrompts | undefined;

  constructor(private store: Store, private featureFlagService: FeatureFlagService) { }

  ngOnInit(): void {
    combineLatest([
      this.store.select(selectStagedTransactionType),
      this.store.select(selectTransferType)
    ])
      .pipe(takeUntil(this.subscriptionNotifier))
      .subscribe(([transactionType, transferType]) => {
        this.transactionType = transactionType;
        this.useMultipleIt = (transferType === transferTypes.POSITIONS || transferType === transferTypes.POSITIONS_CASH) ?? false;
      });

      this.useSloa$ = this.store.select(selectUseStandingAuth);
      this.useSloa$.pipe(takeUntil(this.destroy$)).subscribe(useStandingAuth => this.useStandingAuth = useStandingAuth);
      this.store.select(selectStagedTransactionFormItExtraPrompts).pipe(takeUntil(this.destroy$)).subscribe(extraPrompts => {
          this.extraPrompts = extraPrompts;
      });
      this.store.select(selectHasPositionLotsPhase2).pipe(takeUntil(this.subscriptionNotifier)).subscribe(val => {
        this.positionLotsPhase2EnabledFsg = val;
      });

    combineLatest([
      this.store.select(selectIsISOCompliant)
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([isoCompliant]) => {
          this.isISOCompliant = isoCompliant;

      });

      this.store.select(selectHasAccessInitiateMoneyMovement)
      .pipe(takeUntil(this.destroy$))
      .subscribe(response => {
        this.hasMmInitiateAccess = response;
      });

      this.store.select(selectPendForRelease).pipe(takeUntil(this.destroy$)).subscribe(shouldPendForRelease => {
        this.isPendForRelease = shouldPendForRelease;
      });

      this.store.select(selectInitReleaseTransactionInContext).pipe(takeUntil(this.destroy$)).subscribe(transactionInContext => {
        this.isInRTxnInContext = !!transactionInContext.draftId;
      });

      this.store.select(selectViewInContext).pipe(takeUntil(this.destroy$)).subscribe(step => {
        this.currentStep = step;
      });

    if (this.isTemplateView) {
      //select template in context
      combineLatest([
        this.store.select(selectTemplatesList),
        this.store.select(selectTransactionsTableCurrentTemplateId)
      ]).pipe(takeUntil(this.destroy$)).subscribe(([templates, templateId]) => {
        const templateInContext = templates.find(template => template.draftId === templateId);
        if (templateInContext) {
          this.isISOCompliantTemplate = templateInContext.draftObject.isIsoCompliant;
        }
      });
    }
  }

  ngOnDestroy(): void {
    this.subscriptionNotifier.next(true)
    this.subscriptionNotifier.unsubscribe()
  }

  get isViewPage(): boolean {
    return this.currentStep === pageInContext.view;
  }

  get isInitiateAndReleaseFlowFromTable(): boolean {
    // InR txn is in context when user clicks on view/review on pending request table. Txn will not be in context during
    // input to submit flow or during view flow
    return this.hasMmInitiateAccess && !this.isPendForRelease && this.isInRTxnInContext;
  }

  loadTransactionDetailsWireIsoComponent(): boolean {
    if (this.useStandingAuth || this.isInitiateAndReleaseFlowFromTable
      || this.currentStep === pageInContext.view || this.currentStep === pageInContext.viewMsv) {

      return this.transactionType === 'wire' && (this.isISOCompliant ?? false);

    }

    if (this.isTemplateView) {
      return this.isISOCompliantTemplate;
    }

    return this.transactionType === 'wire';

  }
}
